import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import { INPUT_POLICY_PATH } from '../../policy_feature/inputs'

// `refs` describes one name-matched requirement domain: which inputs carry it and how its
// "<prefix>:<name>" sentinel is read and written. Callers pass a module-scope constant so it is
// stable across renders.
const deriveInitialState = (policyBody, refs) => {
  const autoModeInputs = []
  const requirementIds = {}

  Object.keys(INPUT_POLICY_PATH)
    .filter(refs.isInput)
    .forEach((input) => {
      const values = get(policyBody, INPUT_POLICY_PATH[input]) || []
      const references = values.filter(refs.isRequirementRef).map(refs.getRequirementId)
      if (references.length) {
        autoModeInputs.push(input)
        requirementIds[input] = references
      }
    })

  return { autoModeInputs, requirementIds }
}

export const getAutoModeNameInputs = (policyBody, refs) => deriveInitialState(policyBody, refs).autoModeInputs

export const buildNameRequirementOptionsMap = (values, refs) =>
  Object.fromEntries((values || []).filter(refs.isRequirementRef).map((value) => [value, refs.getRequirementId(value)]))

// Owns whether a field is resolved from requirements matched by name at deploy or still
// prompted. Values on the field that are not requirement refs (built-in ids or selectors) are
// kept when the attached requirements change.
export default function useNameFieldRequirements({ initialPolicyBody, setPolicyBody, refs }) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() => deriveInitialState(initialPolicyBody, refs))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  // Builds on React's pending policy body (functional setState) rather than a render-time
  // snapshot, so clearing include and exclude in the same pass keeps both edits.
  const writeFieldValue = (input, buildValues) => {
    setPolicyBody((previousPolicyBody) => {
      const updated = cloneDeep(previousPolicyBody) || {}
      set(updated, INPUT_POLICY_PATH[input], buildValues(get(previousPolicyBody, INPUT_POLICY_PATH[input]) || []))
      return updated
    })
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, () => [])
    }
  }

  const setAttachedRequirementIds = (input, updatedIds) => {
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(input, (currentValues) => [
      ...currentValues.filter((value) => !refs.isRequirementRef(value)),
      ...updatedIds.map(refs.toRequirementRef),
    ])
  }

  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody, refs))
  }

  return { isAutoMode, getAttachedRequirementIds, toggleAutoMode, setAttachedRequirementIds, hydrate }
}
